import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Brand } from '@/components/Brand';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Menu, X, ArrowRight, Wrench, PackageSearch, Phone } from 'lucide-react';

const SUPPORT_PHONE = '+91-94227-99760';

/* Public header, footer and the login/register portal chooser.
   Shared by the homepage and the /sell seller landing page — one chrome, one theme. */

export function PublicNav({ onChoose, active }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('homepage');
  const links = [
    { key: 'buyers', to: '/buy', label: t('nav.forGarages') },
    { key: 'sellers', to: '/sell', label: t('nav.forSuppliers') },
  ];
  const cls = (key) => (active === key
    ? 'text-sm font-semibold text-graphite-900'
    : 'text-sm font-medium text-graphite-700 hover:text-graphite-900 transition-colors duration-150');
  const bar = (key) => (key === 'sellers' ? 'bg-supplier-500' : 'bg-amber-500');

  return (
    <header className="bg-white border-b border-graphite-border">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-home" className="flex items-center gap-2">
          <Brand className="h-8 w-auto" testid="nav-brand" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.key} to={l.to} data-testid={`nav-${l.key}`} className={cls(l.key)}>
              {l.label}
              {active === l.key && <span className={`block h-0.5 mt-1 rounded-full ${bar(l.key)}`} />}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher variant="link" />
          <div className="w-px h-5 bg-graphite-border mx-1" />
          <button type="button" onClick={() => onChoose('login')} data-testid="nav-login"
                  className="text-sm font-semibold text-graphite-700 hover:text-graphite-900 px-3 py-2 transition-colors">
            {t('nav.login')}
          </button>
          <button type="button" onClick={() => onChoose('register')} data-testid="nav-register"
                  className="accent-btn h-10 px-5">
            {t('nav.register')}
          </button>
        </div>

        <button
          data-testid="nav-menu-toggle"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-graphite-700 hover:bg-paper-200"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-graphite-border bg-white">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.key} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-${l.key}-mobile`}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-graphite-700 hover:bg-paper-200">
                {l.label}
              </Link>
            ))}
            <div className="px-3 py-2.5"><LanguageSwitcher variant="pill" /></div>
            <div className="grid grid-cols-2 gap-2 pt-2 mt-2 border-t border-graphite-border">
              <button type="button" data-testid="nav-login-mobile"
                      onClick={() => { setOpen(false); onChoose('login'); }}
                      className="ghost-btn h-11 justify-center">{t('nav.login')}</button>
              <button type="button" data-testid="nav-register-mobile"
                      onClick={() => { setOpen(false); onChoose('register'); }}
                      className="accent-btn h-11 justify-center">{t('nav.register')}</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-graphite-500 mb-4">{title}</div>
      <ul className="space-y-3">
        {links.map(([label, href]) => (
          <li key={label}>
            {href.startsWith('/') ? (
              <Link to={href} className="text-sm text-graphite-700 hover:text-graphite-900 transition-colors">{label}</Link>
            ) : (
              <a href={href} className="text-sm text-graphite-700 hover:text-graphite-900 transition-colors">{label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PublicFooter() {
  const { t } = useTranslation('homepage');
  return (
    <footer id="contact" className="bg-white border-t border-graphite-border">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
          <div>
            <Brand variant="vertical" className="h-20 w-auto" testid="footer-brand" />
            <p className="mt-4 text-sm text-graphite-500 leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            <a
              href={`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, '')}`}
              data-testid="footer-phone"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-graphite-900 hover:text-amber-500 transition-colors duration-150"
            >
              <span className="inline-flex w-8 h-8 rounded-xl bg-amber-500/10 items-center justify-center shrink-0">
                <Phone size={14} className="text-amber-500" />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-graphite-400">
                  {t('footer.callUs')}
                </span>
                <span className="block mono whitespace-nowrap">{SUPPORT_PHONE}</span>
              </span>
            </a>
          </div>

          <FooterCol title={t('footer.marketplace')} links={[
            [t('footer.howItWorks'), '/#how-it-works'],
            [t('footer.faq'),        '/#faq'],
            [t('footer.contact'),    'mailto:hello@spareshub.com'],
          ]} />

          <FooterCol title={t('footer.forGarages')} links={[
            ['Post an Enquiry', '/buy'],
            [t('footer.login'),    '/buyer/login'],
            [t('footer.support'),  'mailto:support@spareshub.com'],
          ]} />

          <FooterCol title={t('footer.forSuppliers')} links={[
            [t('footer.becomeSupplier'), '/sell'],
            [t('footer.sellerLogin'),    '/seller/login'],
            [t('footer.support'),        'mailto:sellers@spareshub.com'],
          ]} />

          <FooterCol title={t('footer.company')} links={[
            ['About SparesHub', '/legal/about'],
            ['Contact Us',      '/legal/contact'],
          ]} />

          <FooterCol title={t('footer.policies')} links={[
            ['Terms of Use',                '/legal/terms'],
            ['Buyer Terms & Conditions',    '/legal/buyer-terms'],
            ['Seller Terms & Conditions',   '/legal/seller-terms'],
            ['Privacy Policy',              '/legal/privacy'],
            ['Refund Policy',               '/legal/refund-policy'],
            ['Shipping Policy',             '/legal/shipping-policy'],
            ['Warranty Policy',             '/legal/warranty-policy'],
            ['Legal Disclaimer',            '/legal/disclaimer'],
            ['Delete my account',           '/account-deletion'],
          ]} />
        </div>

        <div className="mt-12 pt-6 border-t border-graphite-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-graphite-500">
          <div>© {new Date().getFullYear()} SparesHub. {t('footer.copyright')}</div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:text-graphite-900">{t('footer.privacy')}</Link>
            <Link to="/legal/terms" data-testid="footer-terms" className="hover:text-graphite-900">{t('footer.terms')}</Link>
            <Link to="/legal/disclaimer" data-testid="footer-disclaimer" className="hover:text-graphite-900">Disclaimer</Link>
            <Link to="/account-deletion" data-testid="footer-account-deletion" className="hover:text-graphite-900">Delete account</Link>
            <Link to="/login" data-testid="footer-staff-login" className="text-graphite-400 hover:text-graphite-900">
              {t('footer.staffLogin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function AuthChooser({ mode, onClose }) {
  const { t } = useTranslation('homepage');
  if (!mode) return null;
  const isLogin = mode === 'login';
  const cards = [
    {
      key: 'garage',
      icon: Wrench,
      to: '/buyer/login',
      tint: 'bg-amber-500/10 text-amber-600',
      title: t('chooser.garageTitle'),
      body: t('chooser.garageBody'),
      cta: isLogin ? t('chooser.garageLogin') : t('chooser.garageRegister'),
      btn: 'accent-btn',
    },
    {
      key: 'supplier',
      icon: PackageSearch,
      to: '/seller/login',
      tint: 'bg-supplier-500/10 text-supplier-500',
      title: t('chooser.supplierTitle'),
      body: t('chooser.supplierBody'),
      cta: t('chooser.supplierLogin'),
      btn: 'supplier-btn',
    },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" data-testid="auth-chooser-modal">
      <button
        type="button"
        aria-label={t('chooser.close')}
        data-testid="auth-chooser-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-graphite-900/60 backdrop-blur-[2px]"
      />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl border border-graphite-border p-5 sm:p-8 shadow-soft-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-graphite-900 tracking-tight">
              {isLogin ? t('chooser.loginTitle') : t('chooser.registerTitle')}
            </h2>
            <p className="mt-1.5 text-sm text-graphite-500">
              {isLogin ? t('chooser.loginBody') : t('chooser.registerBody')}
            </p>
          </div>
          <button
            type="button"
            data-testid="auth-chooser-close"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-xl border border-graphite-border grid place-items-center text-graphite-500 hover:text-graphite-900 hover:bg-paper-200 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-6 space-y-3">
          {cards.map((c) => (
            <div key={c.key} className="border border-graphite-border rounded-2xl p-5 hover:border-graphite-300 transition-colors">
              <div className="flex items-center gap-3">
                <span className={`inline-flex w-10 h-10 rounded-xl items-center justify-center ${c.tint}`}>
                  <c.icon size={18} />
                </span>
                <div className="font-heading font-bold text-base text-graphite-900">{c.title}</div>
              </div>
              <p className="mt-3 text-sm text-graphite-500 leading-relaxed">{c.body}</p>
              <Link
                to={c.to}
                data-testid={`chooser-${c.key}-${isLogin ? 'login' : 'register'}`}
                onClick={onClose}
                className={`${c.btn} mt-4 w-full`}
              >
                {c.cta} <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>

        {!isLogin && (
          <p className="mt-5 text-xs text-graphite-500 leading-relaxed" data-testid="chooser-supplier-note">
            {t('chooser.supplierNote')}
          </p>
        )}
        <div className="mt-4 text-xs text-graphite-400">
          <Link to="/login" data-testid="chooser-staff-login" className="hover:text-graphite-700">
            {t('footer.staffLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
